import styled, { css } from 'styled-components';

import { getUnitatBgColor, getUnitatColor } from '../../utils/unitats';
import {
	PageTitle,
	WideContentContainer,
} from '../Shared/CommonStyles';

// Theme variants - all outline-based
export type ButtonTheme = 'outline-basic' | 'outline-rounded' | 'outline-fill' | 'outline-subtle' | 'outline-thick';

export const SelectionContainer = styled(WideContentContainer)`
	text-align: center;
	padding-top: 40px;
`;

export const SelectionPageTitle = styled(PageTitle)`
	text-align: center;
	margin-bottom: 28px;
`;

export const ButtonGrid = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: 28px;
	max-width: 800px;
	margin: 0 auto;

	@media (max-width: 640px) {
		gap: 20px;
	}
`;

// Make buttons have consistent width and center in their row
export const ButtonGridItem = styled.div`
	flex: 1 1 240px;
	max-width: 240px;
	display: flex;
	justify-content: center;
`;


interface UnitatSelectionButtonProps {
	unitat: string;
}

const baseButtonStyles = css`
	padding: 24px 20px;
	border-radius: 12px;
	font-size: 0.95em;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.25s ease;
	min-height: 110px;
	width: 100%;
	max-width: 240px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 6px;

	@media (max-width: 640px) {
		padding: 20px 16px;
		min-height: 100px;
	}
`;

// Theme 1: Outline Basic - transparent bg, colored border, colored text
const outlineBasicStyles = ({ unitat }: UnitatSelectionButtonProps) => css`
	border: 2px solid ${getUnitatColor(unitat)};
	background: transparent;
	color: ${getUnitatColor(unitat)};

	&:hover {
		background: ${getUnitatBgColor(unitat)};
		transform: translateY(-2px);
	}
`;

// Theme 2: Outline Rounded - fully rounded corners
const outlineRoundedStyles = ({ unitat }: UnitatSelectionButtonProps) => css`
	border: 2px solid ${getUnitatColor(unitat)};
	background: transparent;
	color: ${getUnitatColor(unitat)};
	border-radius: 50px;
	min-height: 100px;

	&:hover {
		background: ${getUnitatBgColor(unitat)};
		transform: translateY(-2px);
	}
`;

// Theme 3: Outline Fill - fills on hover
const outlineFillStyles = ({ unitat }: UnitatSelectionButtonProps) => css`
	border: 2px solid ${getUnitatColor(unitat)};
	background: transparent;
	color: ${getUnitatColor(unitat)};

	&:hover {
		background: ${getUnitatColor(unitat)};
		color: white;
		transform: translateY(-2px);
	}
`;

// Theme 4: Outline Subtle - lighter colors, more subtle hover
const outlineSubtleStyles = ({ unitat }: UnitatSelectionButtonProps) => css`
	border: 2px solid ${getUnitatColor(unitat)}40;
	background: ${getUnitatBgColor(unitat)};
	color: ${getUnitatColor(unitat)};
	border-radius: 16px;

	&:hover {
		border-color: ${getUnitatColor(unitat)};
		background: ${getUnitatBgColor(unitat)};
		transform: translateY(-2px);
	}
`;

// Theme 5: Outline Thick - thicker border
const outlineThickStyles = ({ unitat }: UnitatSelectionButtonProps) => css`
	border: 3px solid ${getUnitatColor(unitat)};
	background: transparent;
	color: ${getUnitatColor(unitat)};

	&:hover {
		background: ${getUnitatBgColor(unitat)};
		transform: translateY(-2px);
	}
`;

export const UnitatSelectionButton = styled.button<UnitatSelectionButtonProps & { $variant?: ButtonTheme }>`
	${baseButtonStyles}
	${({ $variant }) => {
		switch ($variant) {
			case 'outline-rounded':
				return outlineRoundedStyles;
			case 'outline-fill':
				return outlineFillStyles;
			case 'outline-subtle':
				return outlineSubtleStyles;
			case 'outline-thick':
				return outlineThickStyles;
			case 'outline-basic':
			default:
				return outlineBasicStyles;
		}
	}}
`;

export const ButtonLabel = styled.span`
	font-size: 1em;
	line-height: 1.3;
	text-align: center;
`;

export const ButtonSubLabel = styled.span`
	font-size: 0.7em;
	font-weight: 500;
	opacity: 0.7;
	margin-top: 2px;
	text-align: center;
`;

const baseAllUnitsStyles = css`
	${baseButtonStyles}
`;

const outlineBasicAllStyles = css`
	border: 2px solid #1f1099;
	background: transparent;
	color: #1f1099;

	&:hover {
		background: #eeeeff;
		transform: translateY(-2px);
	}
`;

const outlineRoundedAllStyles = css`
	border: 2px solid #1f1099;
	background: transparent;
	color: #1f1099;
	border-radius: 50px;
	min-height: 100px;

	&:hover {
		background: #eeeeff;
		transform: translateY(-2px);
	}
`;

const outlineFillAllStyles = css`
	border: 2px solid #1f1099;
	background: transparent;
	color: #1f1099;

	&:hover {
		background: #1f1099;
		color: white;
		transform: translateY(-2px);
	}
`;

const outlineSubtleAllStyles = css`
	border: 2px solid #1f109940;
	background: #eeeeff;
	color: #1f1099;
	border-radius: 16px;

	&:hover {
		border-color: #1f1099;
		transform: translateY(-2px);
	}
`;

const outlineThickAllStyles = css`
	border: 3px solid #1f1099;
	background: transparent;
	color: #1f1099;

	&:hover {
		background: #eeeeff;
		transform: translateY(-2px);
	}
`;

export const AllUnitsButton = styled.button<{ $variant?: ButtonTheme }>`
	${baseAllUnitsStyles}
	${({ $variant }) => {
		switch ($variant) {
			case 'outline-rounded':
				return outlineRoundedAllStyles;
			case 'outline-fill':
				return outlineFillAllStyles;
			case 'outline-subtle':
				return outlineSubtleAllStyles;
			case 'outline-thick':
				return outlineThickAllStyles;
			case 'outline-basic':
			default:
				return outlineBasicAllStyles;
		}
	}}
`;

export const BackLink = styled.a`
	display: inline-block;
	margin-top: 40px;
	color: #3498db;
	font-size: 0.95em;
	cursor: pointer;
	text-decoration: none;

	&:hover {
		text-decoration: underline;
	}
`;
